import { FC } from "react";

import { Box } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";

import { FullWidthVStack } from "@components/common";
import EmailInput from "@components/EmailInput";
import Icon from "@components/Icon";
import Link from "@components/Link";
import { useMessage } from "@components/Message";
import PhoneInput from "@components/PhoneInput";
import TextInput from "@components/TextInput";
import { useRouter } from "@lib/router";
import { AddressInputs } from "@modules/address-search";
import { DOMAIN, ROUTES } from "@utils/constants";

import SectionTitle from "../../../components/common/SectionTitle";
import { OrderFields, ORDER_MESSAGE_ZONE } from "../../../constants";
import useOrderFormik from "../../../hooks/useOrderFormik";

type TAddressFormProps = {
  country: string;
};

const AddressForm: FC<TAddressFormProps> = ({ country }) => {
  const { t } = useTranslation();

  const { defaultLocale } = useRouter();
  const { values } = useOrderFormik();

  const setMessage = useMessage();

  const handleAddressSearchError = (error?: string) => {
    setMessage({
      message: error || t("order-message-failed_locker_search"),
      props: {
        variant: "error",
        icon: <Icon icon="warning" size="1.45rem" />,
        canClose: true,
        padding: "0.45rem 0.75rem",
      },
      zone: ORDER_MESSAGE_ZONE,
    });
  };

  return (
    <FullWidthVStack spacing="1.25rem">
      <FullWidthVStack spacing="0.5rem">
        <Box data-field={OrderFields.NEW_ADDRESS_NAME}>
          <TextInput name={OrderFields.NEW_ADDRESS_NAME} label={t("order-receiver-name")} id="order-receiver-name" />
        </Box>
        <Box data-field={OrderFields.NEW_ADDRESS_PHONE}>
          <PhoneInput
            name={OrderFields.NEW_ADDRESS_PHONE}
            label={t("order-receiver-mobile_number")}
            id="order-new_receiver-phone"
            pinnedPhoneCodes={DOMAIN.DOMAIN_PHONE_NUMBERS[defaultLocale!]}
            defaultCountry={values[OrderFields.DESTINATION_COUNTRY]!}
          />
        </Box>
        <Box data-field={OrderFields.NEW_ADDRESS_EMAIL}>
          <EmailInput
            name={OrderFields.NEW_ADDRESS_EMAIL}
            label={`${t("order-receiver-email")} ${t("common-optional")}`}
            id="order-new_receiver-email"
          />
        </Box>
      </FullWidthVStack>
      <FullWidthVStack spacing="0.5rem">
        <SectionTitle>{t("order-receiver-address_details")}</SectionTitle>
        <Box data-field={OrderFields.DESTINATION_COUNTRY_NAME}>
          <TextInput
            name={OrderFields.DESTINATION_COUNTRY_NAME}
            isReadOnly
            rightAddon={<Link label="Edit" href={ROUTES.ORDER} id="order-new_receiver-country_edit" />}
            id="order-new_receiver-country"
          />
        </Box>
        <AddressInputs
          id="order-new_receiver-address"
          countryCode={country}
          streetFieldName={OrderFields.NEW_ADDRESS_STREET}
          houseNoFieldName={OrderFields.NEW_ADDRESS_HOUSE_NO}
          flatNoFieldName={OrderFields.NEW_ADDRESS_FLAT_NO}
          cityFieldName={OrderFields.NEW_ADDRESS_CITY}
          zipCodeFieldName={OrderFields.NEW_ADDRESS_ZIP_CODE}
          isAddressSelectedFromAddressBook={false}
          onError={handleAddressSearchError}
        />
      </FullWidthVStack>
    </FullWidthVStack>
  );
};
export default AddressForm;
